import { isEmpty } from "lodash-es";
import { FormControl } from "@ecco/ui/form-control";
import { FormErrorMessage } from "@ecco/ui/form-error";
import { FormLabel } from "@ecco/ui/form-label";
import { getNameFromCountryValue } from "@lib/utils/getStateNameFromValue";
import { Box } from "@ecco/ui/box";
import { styles } from "./baseFormStyles";
import type { SingleFieldProps } from "./baseFormsValidations";
import { getStates } from "@components/commerce/baseForms/helpers/helpers";
import type { CountryCode } from "@config/site/types";
import type { SystemStyleObject } from "@ecco/ui/types";
import type { FormikHelpers } from "formik";
import { ResponsiveDropdownField } from "./ResponsiveDropdown/ResponsiveDropdownField";

type regionFieldExtensions = {
    prefix: string;
    setFieldValue: FormikHelpers<unknown>["setFieldValue"];
    value: string;
    title: string;
    style?: SystemStyleObject;
    stateDropdownStyle?: object;
    country?: CountryCode;
    locale?: string;
};

interface RegionFieldType extends SingleFieldProps, regionFieldExtensions {}

export interface State {
    name: string;
    value: string;
    alias?: string;
    englishName?: string;
}

export const RegionField = ({
    error,
    isInvalid,
    isSuccess,
    prefix,
    value,
    country,
    locale,
    title,
    header,
    setFieldValue,
    style,
    stateDropdownStyle,
    isReadOnly = false,
    testId,
}: RegionFieldType) => {
    if (!country || !locale) {
        return null;
    }
    const countryStates = getStates(country, locale);
    const fieldName = prefix + "region";
    if (!countryStates.length) {
        return null;
    }

    const countryStatesSorted =
        country === "JP"
            ? countryStates.sort(
                  (a: State, b: State) => parseInt(a.value, 10) - parseInt(b.value, 10)
              )
            : countryStates.sort((a: State, b: State) => a.name.localeCompare(b.name));

    return (
        <Box sx={style ?? styles.fieldsWrapper}>
            <FormControl
                isInvalid={isInvalid}
                isRequired
                isReadOnly={isReadOnly}
                isSuccess={isSuccess}
            >
                <FormLabel htmlFor={fieldName} sx={styles.formLabel}>
                    {header}
                </FormLabel>
                <ResponsiveDropdownField
                    country={country}
                    locale={locale}
                    setFieldValue={setFieldValue}
                    title={isEmpty(value) ? title : getNameFromCountryValue(value, country, locale)}
                    fieldName={fieldName}
                    isReadOnly={isReadOnly}
                    testId={testId}
                    error={error}
                    dropdownStyle={stateDropdownStyle}
                    items={countryStatesSorted}
                    dropdownPlacement="top"
                />
                <FormErrorMessage>{error}</FormErrorMessage>
            </FormControl>
        </Box>
    );
};

export default RegionField;
